import { Component, Input, OnInit } from "@angular/core";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Chart, registerables } from "chart.js";
import { VoyageReportCiiRatingData } from "@app/services/api/api.models";
import { CiiRanges } from "@app/services/api/api.models";

@Component({
  selector: "app-cii-rating-diagram",
  templateUrl: "./cii-rating-diagram.component.html",
  styleUrls: ["./cii-rating-diagram.component.scss"],
})
export class CiiRatingDiagramComponent implements OnInit {
  @Input() ciiRatingValues: VoyageReportCiiRatingData;

  ciiRanges: CiiRanges;
  ciiRatingChartData: any;
  ciiRatingChartOptions: any;
  ciiRatingChartPlugins: any;

  ciiRatingBefore: string;
  aerBefore_gtnm: number;
  ciiRatingAfter: string;
  aerAfter_gtnm: number;

  ciiRatingColors: { [key: string]: string } = {
    A: "#9BC56D",
    B: "#C6DEAB",
    C: "#FDE399",
    D: "#EE9DA3",
    E: "#E1535D",
  };

  ngOnInit(): void {
    this.ciiRatingBefore = this.ciiRatingValues.ciiRatingBefore;
    this.aerBefore_gtnm = this.ciiRatingValues.aerBefore_gtnm;
    this.ciiRatingAfter = this.ciiRatingValues.ciiRatingAfter;
    this.aerAfter_gtnm = this.ciiRatingValues.aerAfter_gtnm;

    Chart.register(...registerables);

    const averageRangeSize = (this.ciiRatingValues.ciiRanges[3].maxAer_gCO2_per_tnm - this.ciiRatingValues.ciiRanges[0].minAer_gCO2_per_tnm) / 4;
    let eMax = this.ciiRatingValues.ciiRanges[3].maxAer_gCO2_per_tnm + averageRangeSize;
    if (this.ciiRatingValues.aerBefore_gtnm > eMax || this.ciiRatingValues.aerAfter_gtnm > eMax) {
      eMax = Math.max(this.ciiRatingValues.aerBefore_gtnm, this.ciiRatingValues.aerAfter_gtnm);
    }

    const ranges = this.ciiRatingValues.ciiRanges;

    this.ciiRanges = {
      a: getRange("A"),
      b: getRange("B"),
      c: getRange("C"),
      d: getRange("D"),
      e: [getRange("D")[1], eMax],
    };

    function getRange(rating: string): [number, number] {
      const range = ranges.find((r) => r.rating === rating);
      return range ? [range.minAer_gCO2_per_tnm, range.maxAer_gCO2_per_tnm] : [0, 0];
    }

    this.ciiRatingChartData = {
      labels: ["A", "B", "C", "D", "E"],
      datasets: [
        {
          data: [
            this.ciiRanges.a[1] - this.ciiRanges.a[0],
            this.ciiRanges.b[1] - this.ciiRanges.b[0],
            this.ciiRanges.c[1] - this.ciiRanges.c[0],
            this.ciiRanges.d[1] - this.ciiRanges.d[0],
            this.ciiRanges.e[1] - this.ciiRanges.e[0],
          ],
          backgroundColor: ["#9BC56D", "#C6DEAB", "#FDE399", "#EE9DA3", "#E1535D"],
          borderWidth: 0,
        },
      ],
    };

    this.ciiRatingChartOptions = {
      responsive: true,
      cutout: "50%",
      rotation: -90,
      circumference: 180,
      maintainAspectRatio: false,
      layout: {
        padding: {
          bottom: 6,
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          color: "#000",
          font: {
            weight: "normal",
          },
          formatter: (value: number, context: any) => {
            const labels = ["A", "B", "C", "D", "E"];
            return labels[context.dataIndex];
          },
        },
        tooltip: {
          enabled: true,
          callbacks: {
            label: (context: any) => {
              const ratingLetters = ["A", "B", "C", "D", "E"];
              const ranges = this.ciiRanges;

              const index = context.dataIndex;
              const label = ratingLetters[index];
              const [min, max] = ranges[label.toLowerCase() as keyof typeof ranges];

              return `${min.toFixed(1)} – ${max.toFixed(1)}`;
            },
          },
        },
      },
      animation: false,
      pluginsCustom: {
        beforeVoyageIndex: this.getCiiIndexPosition(this.ciiRatingValues.aerBefore_gtnm),
        afterVoyageIndex: this.getCiiIndexPosition(this.ciiRatingValues.aerAfter_gtnm),
      },
    };
    this.ciiRatingChartPlugins = [this.arrowPlugin(), ChartDataLabels];
  }
  arrowPlugin() {
    return {
      id: "arrowPointer",
      afterDatasetsDraw(chart) {
        const { ctx } = chart;

        const centerX = chart._metasets[0].data[0].x;
        const centerY = chart._metasets[0].data[0].y;

        const radius = chart._metasets[0].data[0].outerRadius - 0;
        const anglePerPart = Math.PI / 5;

        const drawArrow = (index: number, color: string) => {
          const angle = Math.PI + anglePerPart * index + anglePerPart / 2;

          const baseWidth = radius * 0.08;
          const arrowLength = radius;

          const dirX = Math.cos(angle);
          const dirY = Math.sin(angle);

          const tipX = centerX + arrowLength * dirX;
          const tipY = centerY + arrowLength * dirY;

          const baseLeftX = centerX + (baseWidth / 2) * -dirY;
          const baseLeftY = centerY + (baseWidth / 2) * dirX;

          const baseRightX = centerX + (baseWidth / 2) * dirY;
          const baseRightY = centerY + (baseWidth / 2) * -dirX;

          ctx.beginPath();
          ctx.moveTo(baseLeftX, baseLeftY);
          ctx.lineTo(baseRightX, baseRightY);
          ctx.lineTo(tipX, tipY);
          ctx.closePath();
          ctx.fillStyle = color;
          ctx.fill();
          ctx.fillStyle = color;
        };

        const config = chart.options.pluginsCustom;
        if (config) {
          if (config.beforeVoyageIndex != null) {
            drawArrow(config.beforeVoyageIndex, "#B3B3B3");
          }
          if (config.afterVoyageIndex != null) {
            drawArrow(config.afterVoyageIndex, "#404040");
          }
        }

        ctx.beginPath();
        ctx.arc(centerX, centerY, (radius * 0.08) / 2, 0, 2 * Math.PI);
        ctx.fillStyle = config.afterVoyageIndex == null ? "#B3B3B3" : "#404040";
        ctx.fill();
      },
    };
  }

  getCiiIndexPosition(value: number | null | undefined): number | null {
    if (value == null) {
      return null;
    }
    const ranges = this.ciiRanges;

    const globalMin = ranges.a[0];
    const globalMax = ranges.e[1];

    const clamped = Math.min(Math.max(value, globalMin), globalMax);

    const totalScale = globalMax - globalMin;
    const fraction = (clamped - globalMin) / totalScale;

    return -0.5 + fraction * 5;
  }
}
